import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useLayoutEffect,
} from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import ToggleMenuIcon from '../Icons/ToggleMenuIcon';
import BuildBaseIcon from '../Icons/BuildBaseIcon';
import { getMenuContent } from '../api-sanity/api';
import '../styles/components/menu.css';
import { useTheme } from '../hooks/useTheme';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollSmoother } from 'gsap/ScrollSmoother';
import { isMobileDevice } from 'utils/helpers';
import useMediaQuery from 'hooks/useMediaQuery';

gsap.registerPlugin(ScrollTrigger, ScrollSmoother);

const WHITE_ICON_COLOR = '#FFFFFF';
const BLUE_ICON_COLOR = 'var(--color-primary-shade-60)';

const backgroundLuminance = (rgbString) => {
  const match = rgbString && rgbString.match(/rgba?\(([^)]+)\)/);
  if (!match) return null;
  const [r, g, b, a = 1] = match[1].split(',').map((n) => parseFloat(n));
  if (!a) return null;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
};

const extractBackgroundImageUrl = (style) => {
  const bg = style.backgroundImage;
  if (!bg || bg === 'none') return null;
  const match = bg.match(/url\(["']?([^"')]+)["']?\)/);
  return match ? match[1] : null;
};

const imageLuminanceCache = new Map();
const IMAGE_SAMPLE_SIZE = 8;

const loadImageLuminance = (url, backdropLuminance) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = IMAGE_SAMPLE_SIZE;
        canvas.height = IMAGE_SAMPLE_SIZE;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, IMAGE_SAMPLE_SIZE, IMAGE_SAMPLE_SIZE);
        const { data } = ctx.getImageData(
          0,
          0,
          IMAGE_SAMPLE_SIZE,
          IMAGE_SAMPLE_SIZE,
        );

        let total = 0;
        const pixelCount = data.length / 4;
        for (let i = 0; i < data.length; i += 4) {
          const alpha = data[i + 3] / 255;
          const luminance =
            (0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]) /
            255;
          total += luminance * alpha + backdropLuminance * (1 - alpha);
        }

        resolve(total / pixelCount);
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });

const getImageLuminance = (url, backdropLuminance) => {
  const cacheKey = `${url}|${backdropLuminance.toFixed(2)}`;
  if (!imageLuminanceCache.has(cacheKey)) {
    imageLuminanceCache.set(cacheKey, null);
    loadImageLuminance(url, backdropLuminance).then((luminance) => {
      imageLuminanceCache.set(cacheKey, luminance);
    });
    return null;
  }
  return imageLuminanceCache.get(cacheKey);
};

const pointIsWithinRect = (x, y, rect) =>
  x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;

const findCoveringImageUrl = (container, x, y) => {
  for (const img of container.querySelectorAll('img')) {
    if (pointIsWithinRect(x, y, img.getBoundingClientRect())) {
      return img.currentSrc || img.src || null;
    }
  }
  return null;
};


const findAncestorBackgroundLuminance = (el, fallbackIsDark) => {
  for (let node = el.parentElement; node; node = node.parentElement) {
    if (node.closest?.('.menu-header')) break;
    const luminance = backgroundLuminance(getComputedStyle(node).backgroundColor);
    if (luminance !== null) return luminance;
  }
  return fallbackIsDark ? 0 : 1;
};

const isDarkBehindPoint = (x, y, fallbackIsDark) => {
  if (typeof document.elementsFromPoint !== 'function') return fallbackIsDark;

  for (const el of document.elementsFromPoint(x, y)) {
    if (el.closest('.menu-header')) continue;

    const style = getComputedStyle(el);

    const colorLuminance = backgroundLuminance(style.backgroundColor);
    if (colorLuminance !== null) return colorLuminance < 0.5;

    const imageUrl =
      el.tagName === 'IMG'
        ? el.currentSrc || el.src
        : extractBackgroundImageUrl(style) || findCoveringImageUrl(el, x, y);
    if (imageUrl) {
      const backdropLuminance = findAncestorBackgroundLuminance(
        el,
        fallbackIsDark,
      );
      const imageLuminance = getImageLuminance(imageUrl, backdropLuminance);
      if (imageLuminance !== null) return imageLuminance < 0.5;
    }
  }

  return fallbackIsDark;
};

const Menu = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobileSize = useMediaQuery();
  const [isExpanded, setIsExpanded] = useState(false);
  const { isDarkMode } = useTheme();
  const [menuData, setMenuData] = useState(null);
  const [currentImagePath, setCurrentImagePath] = useState(null);
  const [isFirstExpansion, setIsFirstExpansion] = useState(true);
  const [preloadedImages, setPreloadedImages] = useState({});
  const menuRef = useRef(null);
  const menuLinksRef = useRef([]);
  const imageContainerRef = useRef(null);
  const toggleMenuIconRef = useRef(null);
  const buildIconRef = useRef(null);
  const lastAnimatedImageRef = useRef(null);
  const scrollProgressBeforeExpand = useRef(0);
  const [toggleIconColor, setToggleIconColor] = useState(WHITE_ICON_COLOR);

  const getFirstRoute = useCallback(() => {
    const segments = location.pathname.split('/');
    return segments.length > 1 ? `/${segments[1]}` : '/';
  }, [location.pathname]);

  const updateIconColors = useCallback(() => {
    if (buildIconRef.current) {
      const rect = buildIconRef.current.getBoundingClientRect();
      const isDark = isDarkBehindPoint(
        rect.left + rect.width / 2,
        rect.top + rect.height / 2,
        isDarkMode,
      );
      buildIconRef.current.style.color = isDark
        ? WHITE_ICON_COLOR
        : BLUE_ICON_COLOR;
    }

    if (toggleMenuIconRef.current) {
      const rect = toggleMenuIconRef.current.getBoundingClientRect();
      const isDark = isDarkBehindPoint(
        rect.left + rect.width / 2,
        rect.top + rect.height / 2,
        isDarkMode,
      );
      setToggleIconColor(isDark ? WHITE_ICON_COLOR : BLUE_ICON_COLOR);
    }
  }, [isDarkMode]);

  useGSAP(
    () => {
      let frame = 0;
      let lastScrollY = window.scrollY;
      const tick = () => {
        frame += 1;
        if (frame % 3 !== 0) return;
        const scrollY = window.scrollY;
        if (scrollY === lastScrollY) return;
        lastScrollY = scrollY;
        updateIconColors();
      };

      updateIconColors();
      gsap.ticker.add(tick);

      return () => gsap.ticker.remove(tick);
    },
    { dependencies: [updateIconColors] },
  );

  useEffect(() => {
    const fetchMenuData = async () => {
      const data = await getMenuContent();
      if (!data.error) setMenuData(data);
    };
    fetchMenuData();
  }, []);

  useGSAP(
    () => {
      const smoother = ScrollSmoother.get();
      if (smoother) {
        smoother.paused(isExpanded);
      }
    },
    { dependencies: [isExpanded] },
  );

  const getImageUrl = useCallback(
    (path) => {
      if (!menuData) return '';
      const imageMap = {
        '/services': {
          dark: menuData.servicesDarkImageUrl,
          light: menuData.servicesLightImageUrl,
        },
        '/about': {
          dark: menuData.aboutDarkImageUrl,
          light: menuData.aboutLightImageUrl,
        },
        '/': {
          dark: menuData.homeDarkImageUrl,
          light: menuData.homeLightImageUrl,
        },
      };
      return (
        imageMap[path]?.[isDarkMode ? 'dark' : 'light'] ||
        imageMap['/'][isDarkMode ? 'dark' : 'light']
      );
    },
    [isDarkMode, menuData],
  );

  useLayoutEffect(() => {
    if (menuData) {
      const imageUrls = [
        menuData.servicesDarkImageUrl,
        menuData.servicesLightImageUrl,
        menuData.aboutDarkImageUrl,
        menuData.aboutLightImageUrl,
        menuData.homeDarkImageUrl,
        menuData.homeLightImageUrl,
      ].filter(Boolean);

      const loadedImages = {};
      let imagesToLoad = imageUrls.length;

      if (imagesToLoad === 0) {
        setPreloadedImages({});
        return;
      }

      imageUrls.forEach((url) => {
        const img = new Image();
        img.src = url;
        img.onload = () => {
          loadedImages[url] = true;
          imagesToLoad--;
          if (imagesToLoad === 0) {
            setPreloadedImages(loadedImages);
          }
        };
        img.onerror = () => {
          console.error(`Failed to load image: ${url}`);
          imagesToLoad--;
          if (imagesToLoad === 0) {
            setPreloadedImages(loadedImages);
          }
        };
      });
    }
  }, [menuData]);

  useLayoutEffect(() => {
    setCurrentImagePath(getFirstRoute());
    if (isExpanded) {
      if (isFirstExpansion) {
        setIsFirstExpansion(false);
      }
    } else {
      setIsFirstExpansion(true);

      if (imageContainerRef.current) {
        gsap.to(imageContainerRef.current.children, {
          opacity: 0,
          duration: 0.3,
          onComplete: () => {
            imageContainerRef.current.innerHTML = '';
          },
        });
      }
    }
  }, [isExpanded, isFirstExpansion, getFirstRoute]);

  useGSAP(
    () => {
      if (!isExpanded || !menuLinksRef.current) return;

      const validLinks = menuLinksRef.current.filter(Boolean);

      if (validLinks.length === 0) return;

      gsap.set(validLinks, {
        clipPath: 'inset(100% 0 0 0)',
        scale: 0.9,
      });

      const tl = gsap.timeline();

      tl.to(validLinks, {
        clipPath: 'inset(0% 0 0 0)',
        duration: 0.9,
        ease: 'power2.out',
        stagger: 0.05,
        overwrite: 'auto',
      }).to(
        validLinks,
        {
          scale: 1,
          duration: 0.9,
          ease: 'power2.out',
          stagger: 0.05,
          overwrite: 'auto',
        },
        '<',
      );

      return () => {
        tl.kill();
      };
    },
    { dependencies: [isExpanded], scope: menuLinksRef },
  );

  useGSAP(
    () => {
      if (!currentImagePath || !imageContainerRef.current || !isExpanded)
        return;

      const newUrl = getImageUrl(currentImagePath);
      const imageContainerEl = imageContainerRef.current;

      if (!preloadedImages[newUrl] && newUrl) {
        console.warn(`Image not yet preloaded: ${newUrl}. Skipping animation.`);
        return;
      }

      const newImageEl = document.createElement('img');
      newImageEl.src = newUrl;
      newImageEl.alt = 'Menu background';
      newImageEl.style.position = 'absolute';
      newImageEl.style.zIndex = imageContainerEl.children.length + 1;

      imageContainerEl.appendChild(newImageEl);

      const tl = gsap.timeline({
        onComplete: () => {
          if (!newImageEl.isConnected) return;
          gsap.set(newImageEl, { opacity: 1, zIndex: 1 });
          lastAnimatedImageRef.current = newImageEl;
        },
      });

      tl.fromTo(
        newImageEl,
        {
          clipPath: 'inset(100% 0 0 0)',
          scale: 0.9,
          opacity: 1,
        },
        {
          clipPath: 'inset(0% 0 0 0)',
          scale: 1,
          duration: 0.9,
          ease: 'power2.out',
          overwrite: 'auto',
        },
      );

      return () => {
        tl.kill();

        if (newImageEl.parentNode === imageContainerEl) {
          imageContainerEl.removeChild(newImageEl);
        }

        if (lastAnimatedImageRef.current === newImageEl) {
          lastAnimatedImageRef.current = null;
        }
      };
    },
    {
      dependencies: [currentImagePath, isExpanded, preloadedImages],
      scope: imageContainerRef,
    },
  );

  useGSAP(() => {
    const mm = gsap.matchMedia(menuRef);

    mm.add(`screen and (min-width: 768px)`, () => {
      if (getFirstRoute() !== '/' || isMobileDevice()) {
        return;
      }

      const icon = buildIconRef.current;
      const menu = menuRef.current;
      const getScrollDistance = () => window.innerHeight * 0.8;

      ScrollTrigger.getById('buildIconPosition')?.kill();

      gsap.set(icon, {
        position: 'absolute',
        width: '51.78dvw',
        height: 'auto',
        y: getScrollDistance(),
        zIndex: 1,
      });

      const tl = gsap.to(icon, {
        y: 0,
        width: '137px',
        ease: 'power2.out',
      });

      ScrollTrigger.create({
        id: 'buildIconPosition',
        animation: tl,
        trigger: menu,
        start: 'top top',
        end: () => `+=${getScrollDistance()}`,
        scrub: 0.1,
        invalidateOnRefresh: false,
        onRefresh: () => {
          const st = ScrollTrigger.getById('buildIconPosition');
          const currentProgress = st?.progress || 0;
          if (currentProgress > 0) {
            tl.progress(currentProgress);
          }
        },
      });

      return () => {
        ScrollTrigger.getById('buildIconPosition')?.kill();
        gsap.set(icon, { clearProps: 'all' });
      };
    });
  }, [getFirstRoute]);

  useGSAP(
    () => {
      const st = ScrollTrigger.getById('buildIconPosition');
      if (!st) {
        return;
      }

      const timeline = st.animation;
      if (!timeline) {
        return;
      }

      gsap.killTweensOf(timeline, 'progress');

      if (isExpanded) {
        scrollProgressBeforeExpand.current = timeline.progress();

        timeline.progress(1);
      } else {
        const targetProgress = scrollProgressBeforeExpand.current;

        gsap.to(timeline, {
          progress: targetProgress,
          duration: 0.6,
          ease: 'power2.inOut',
          overwrite: true,
        });
      }
    },
    { dependencies: [isExpanded] },
  );

  const getLinkClassName = (path) => {
    const isActive = currentImagePath === path;
    return `menu-link ${isActive ? 'menu-link-active' : ''} ${
      isDarkMode ? 'dark' : ''
    }`;
  };

  return (
    <>
      <div ref={menuRef} className='menu-header'>
        <BuildBaseIcon
          ref={buildIconRef}
          height='100%'
          color={WHITE_ICON_COLOR}
          width={isMobileSize ? 160 : 137}
          onClick={() =>
            isExpanded && getFirstRoute() === '/'
              ? setIsExpanded(false)
              : navigate('/')
          }
          className='cursor-pointer'
        />
        <ToggleMenuIcon
          color={toggleIconColor}
          isExpanded={isExpanded}
          onClick={() => setIsExpanded((prev) => !prev)}
          className='cursor-pointer toggle-menu-icon'
          ref={toggleMenuIconRef}
          width={isMobileSize ? 30 : 29}
          height={isMobileSize ? 30 : 29}
        />
      </div>
      {isExpanded && (
        <div className={`menu-content ${isDarkMode ? 'dark' : ''}`}>
          <div className='menu-nav-wrapper'>
            <nav
              className='menu-nav flex'
              onMouseLeave={() => setCurrentImagePath(getFirstRoute())}
            >
              <Link
                ref={(el) => (menuLinksRef.current[0] = el)}
                to='/'
                className={getLinkClassName('/')}
                onMouseEnter={() => setCurrentImagePath('/')}
                onClick={() => setIsExpanded(getFirstRoute() !== '/')}
              >
                {menuData?.homeLink}
              </Link>
              <Link
                ref={(el) => (menuLinksRef.current[1] = el)}
                to='/about'
                className={getLinkClassName('/about')}
                onMouseEnter={() => setCurrentImagePath('/about')}
                onClick={() => setIsExpanded(getFirstRoute() !== '/about')}
              >
                {menuData?.aboutLink}
              </Link>
              <Link
                ref={(el) => (menuLinksRef.current[2] = el)}
                to='/services'
                className={getLinkClassName('/services')}
                onMouseEnter={() => setCurrentImagePath('/services')}
                onClick={() => setIsExpanded(getFirstRoute() !== '/services')}
              >
                {menuData?.servicesLink}
              </Link>
            </nav>
            <div className='menu-footer'>{menuData?.copyrightText}</div>
          </div>
          <div className='image-wrapper'>
            <div
              ref={imageContainerRef}
              className='relative full-height full-width overflow-hidden'
            />
          </div>
        </div>
      )}
    </>
  );
};

export default Menu;
